import React, { useState, useEffect } from "react";

const JoiningDocumentSubmission = () => {
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [leadID, setLeadID] = useState(null);
  const [requestInvoiceId, setRequestInvoiceId] = useState(null);

  useEffect(() => {
    // Extract query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const leadId = urlParams.get("leadID");
    const requestInvoiceId = urlParams.get("requestInvoiceId");
    const selectedDocsParam = urlParams.get("selectedDocuments");

    if (leadId) {
      setLeadID(leadId);
      fetchCandidateDetails(leadId);
    }
    if (requestInvoiceId) {
      setRequestInvoiceId(requestInvoiceId);
    }

    if (selectedDocsParam) {
      try {
        const decodedDocs = JSON.parse(decodeURIComponent(selectedDocsParam));
        setSelectedDocuments(decodedDocs);
      } catch (err) {
        console.error("Error parsing selected documents:", err);
        setError("Error parsing selected documents");
      }
    }
  }, []);

  const fetchCandidateDetails = async (leadId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/leads/lead-candidate-details/${leadId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch candidate details");
      }
      const data = await response.json();
      setCandidateDetails(data.data);
    } catch (err) {
      console.error("Error fetching candidate details:", err);
      setError("Failed to fetch candidate details");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (documentType, file) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [documentType]: file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!leadID) {
      setError("Lead ID is required");
      return;
    }

    // Check if all required documents are uploaded
    const missingDocs = selectedDocuments.filter((doc) => !uploadedFiles[doc]);
    if (missingDocs.length > 0) {
      setError(
        `Please upload all required documents: ${missingDocs.join(", ")}`
      );
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append("leadID", leadID);

      // Append each uploaded file
      selectedDocuments.forEach((docType) => {
        if (uploadedFiles[docType]) {
          formData.append(docType, uploadedFiles[docType]);
        }
      });

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/leads/lead-unboarding-doc-upload/${leadID}/${requestInvoiceId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload documents");
      }

      alert("Documents uploaded successfully!");
      setUploadedFiles({}); // Clear uploaded files after successful submission
    } catch (err) {
      console.error("Error uploading documents:", err);
      setError("Failed to upload documents. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Candidate Details Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Joining Document Submission
          </h1>

          {candidateDetails ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Name</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {candidateDetails.fullName || "N/A"}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Email</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {candidateDetails.email || "N/A"}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Phone Number
                </h3>
                <p className="text-lg font-semibold text-gray-900">
                  {candidateDetails.phoneNumber || "N/A"}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-red-600">Failed to load candidate details</p>
            </div>
          )}
        </div>

        {/* Document Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Upload Required Documents
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div onSubmit={handleSubmit} className="space-y-6">
            {selectedDocuments.length > 0 ? (
              selectedDocuments.map((docType) => (
                <div
                  key={docType}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {docType
                      .replace(/-/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
                    *
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) =>
                      handleFileChange(docType, e.target.files[0])
                    }
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                      border border-gray-300 rounded-md
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {uploadedFiles[docType] && (
                    <p className="mt-2 text-sm text-green-600">
                      ✓ {uploadedFiles[docType].name} selected
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Accepted formats: PDF, JPG, JPEG, PNG, DOC, DOCX (Max size:
                    10MB)
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No documents selected for upload
                </p>
              </div>
            )}

            {selectedDocuments.length > 0 && (
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className={`px-6 py-3 rounded-lg font-medium text-white transition-colors ${
                    uploading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  }`}
                >
                  {uploading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </div>
                  ) : (
                    "Submit Documents"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoiningDocumentSubmission;
